import React, { Fragment, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom/dist";
import Table from "../../core/pagination/datatable";
import { Download, PlusCircle, Upload } from "feather-icons-react/build/IconComponents";
import { ErrorToaster } from "../components/toaster/Toaster";
import InvoiceServices from "../../services/Invoice";
import InvoiceForm from "../../core/modals/invoice/invoiceForm";
import { cleanObject, Debounce, getMonthDate } from "../../utils";
import { useSelector } from "react-redux";
import { all_routes } from "../../Router/all_routes";
import { deleteAlert, confirmationAlert, sweetAlert } from "../components/sweetAlert";
import { DatePicker, Modal } from "antd";
import { Sliders, Calendar, StopCircle, CheckSquare, Trash2, CheckCircle, Send } from "react-feather";
import dayjs from "dayjs";
import Select from "react-select";
import { invoiceStatuses } from "../../core/json/invoiceStatuses";
import ExportInvoices from "../../core/modals/invoice/invoiceExport";

const Invoices = () => {

  const routes = all_routes;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { RangePicker } = DatePicker;

  const { userData } = useSelector((state) => {
    return {
      userData: state.user_detail
    }
  });

  const [loader, setLoader] = useState(false);
  const [validationLoading, setValidationLoading] = useState(false);
  const [submittingLoading, setSubmittingLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);

  const [invoices, setInvoices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkResult, setBulkResult] = useState(null);
  const [resultModalOpen, setResultModalOpen] = useState(false);
  const [resultModalTitle, setResultModalTitle] = useState('');

  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const pageLimit = parseInt(searchParams.get("limit") || "10", 10);
  const searchText = searchParams.get("search") || "";
  const status = searchParams.get("status") || "";
  const startDate = searchParams.get("from") || "";
  const endDate = searchParams.get("to") || "";
  const sort = searchParams.get("sort") || "invoice_ref_no";

  const sortOptions = [
    { label: 'Invoice Ref', value: 'invoice_ref_no' },
    { label: 'Invoice Date', value: 'invoice_date' },
  ];

  const getInvoices = async (page = currentPage, limit = pageLimit) => {
    setLoader(true);
    try {
      const params = {
        ...Object.fromEntries(searchParams.entries()),
        page,
        limit
      };
      const { data } = await InvoiceServices.getInvoices(params);
      setInvoices(data?.invoices);
      setTotalCount(data?.pagination?.total);
      setSelectedIds([]);
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  const deleteInvoice = async (id) => {
    try {
      const isConfirmed = await deleteAlert();
      if (isConfirmed) {
        const { message } = await InvoiceServices.deleteInvoice(id);
        getInvoices();
        sweetAlert("Deleted!", message, "success");
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const validateInvoice = async (id) => {
    setValidationLoading(true);
    try {
      const { message } = await InvoiceServices.validateInvoice({ invoice_id: id });
      sweetAlert("Validated!", message, "success");
    } catch (error) {
      ErrorToaster(error);
    } finally {
      getInvoices();
      setValidationLoading(false);
    }
  };

  const submitInvoice = async (id) => {
    setSubmittingLoading(true);
    try {
      const { message } = await InvoiceServices.submitInvoice({ invoice_id: id });
      sweetAlert("Submitted!", message, "success");
    } catch (error) {
      ErrorToaster(error);
    } finally {
      getInvoices();
      setSubmittingLoading(false);
    }
  };

  // ── Bulk actions ──────────────────────────────────────────
  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === invoices.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(invoices.map((inv) => inv.id));
    }
  };

  const showBulkResult = (title, result) => {
    setResultModalTitle(title);
    setBulkResult(result);
    setResultModalOpen(true);
  };

  // Returns { eligible: [], ineligible: [] } by filtering selected invoices against a predicate
  const partitionSelected = (predicate) => {
    const selected = invoices.filter((inv) => selectedIds.includes(inv.id));
    const eligible = selected.filter(predicate);
    const ineligible = selected.filter((inv) => !predicate(inv));
    return { eligible, ineligible };
  };

  // Show a Swal warning when some invoices are ineligible; returns true to proceed, false to abort
  const confirmWithSkipped = async (action, eligible, ineligible) => {
    if (ineligible.length === 0) return true;
    if (eligible.length === 0) {
      sweetAlert('No eligible invoices', `All selected invoices are already submitted and cannot be ${action}d.`, 'error');
      return false;
    }
    return await confirmationAlert(
      `${ineligible.length} invoice(s) will be skipped`,
      `${ineligible.length} selected invoice(s) are already submitted and will be skipped. ${eligible.length} invoice(s) will be ${action}d. Continue?`,
      'warning'
    );
  };

  const isSubmitted = (inv) => !!(inv.submitted_at || inv.fbr_invoice_number || inv.status === 'SUBMITTED');

  const handleBulkDelete = async () => {
    // Eligible: not submitted
    const { eligible, ineligible } = partitionSelected((inv) => !isSubmitted(inv));
    const proceed = await confirmWithSkipped('delete', eligible, ineligible);
    if (!proceed) return;

    const confirmed = await confirmationAlert(`Delete ${eligible.length} invoice(s)?`, 'This action cannot be undone.', 'warning');
    if (!confirmed) return;

    setBulkLoading(true);
    try {
      const { data } = await InvoiceServices.bulkDelete({ ids: eligible.map((inv) => inv.id) });
      showBulkResult('Bulk Delete Results', data);
      getInvoices();
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkValidate = async () => {
    // Eligible: not submitted and not already VALID
    const { eligible, ineligible } = partitionSelected(
      (inv) => !isSubmitted(inv) && inv.status !== 'VALID'
    );
    const proceed = await confirmWithSkipped('validate', eligible, ineligible);
    if (!proceed) return;

    setBulkLoading(true);
    try {
      const { data } = await InvoiceServices.bulkValidate({ ids: eligible.map((inv) => inv.id) });
      showBulkResult('Bulk Validate Results', data);
      getInvoices();
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkSubmit = async () => {
    // Eligible: status is VALID (only VALID invoices can be submitted)
    const { eligible, ineligible } = partitionSelected(
      (inv) => !isSubmitted(inv) && inv.status === 'VALID'
    );
    const proceed = await confirmWithSkipped('submit', eligible, ineligible);
    if (!proceed) return;

    setBulkLoading(true);
    try {
      const { data } = await InvoiceServices.bulkSubmit({ ids: eligible.map((inv) => inv.id) });
      showBulkResult('Bulk Submit Results', data);
      getInvoices();
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setBulkLoading(false);
    }
  };

  const handlePaginationFilter = (data) => {
    try {
      const updatedParams = {
        ...Object.fromEntries(searchParams.entries()),
        page: 1,
        ...cleanObject(data)
      };
      Debounce(() => setSearchParams(updatedParams));
    } catch (error) {
      console.log('handlePaginationFilter => error:', error);
    }
  };

  const allSelected = invoices.length > 0 && selectedIds.length === invoices.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < invoices.length;

  const columns = [
    {
      title: (
        <input
          type="checkbox"
          checked={allSelected}
          ref={(el) => { if (el) el.indeterminate = someSelected; }}
          onChange={toggleSelectAll}
          style={{ cursor: 'pointer', width: 16, height: 16 }}
        />
      ),
      dataIndex: "id",
      width: 48,
      render: (id) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(id)}
          onChange={() => toggleSelect(id)}
          style={{ cursor: 'pointer', width: 16, height: 16 }}
        />
      ),
    },
    {
      title: "Actions",
      dataIndex: "action",
      render: (text, record) => (
        <div className="action-table-data">
          <button
            type="button"
            className="btn btn-sm btn-primary btn-wave waves-effect waves-light me-2"
            onClick={() => navigate(`${routes?.viewInvoice}/${record?.id}`)}
          >
            View Invoice
          </button>
          {!record?.submitted_at && (
            <>
              {record?.status !== 'SUBMITTED' && record?.status !== 'VALID' && (
                <button
                  type="button"
                  className="btn btn-sm btn-warning btn-wave waves-effect waves-light me-2"
                  disabled={validationLoading}
                  onClick={() => validateInvoice(record?.id)}
                >
                  Validate
                </button>
              )}
              {record?.status === 'VALID' && (
                <button
                  type="button"
                  className="btn btn-sm btn-success btn-wave waves-effect waves-light me-2"
                  disabled={submittingLoading}
                  onClick={() => submitInvoice(record?.id)}
                >
                  Submit
                </button>
              )}
              {record?.status !== 'SUBMITTED' && (
                <button
                  type="button"
                  className="btn btn-sm btn-info btn-wave waves-effect waves-light me-2"
                  onClick={() => navigate(`${routes?.editInvoice}/${record?.id}`)}
                >
                  Edit
                </button>
              )}
              {record?.status !== 'SUBMITTED' && (
                <button
                  type="button"
                  className="btn btn-sm btn-danger btn-wave waves-effect waves-light me-2"
                  onClick={() => deleteInvoice(record?.id)}
                >
                  Delete
                </button>
              )}
            </>
          )}
        </div>
      ),
    },
    {
      title: "Invoice Ref",
      dataIndex: "invoice_ref_no",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Invoice Date",
      dataIndex: "invoice_date",
      render: (text) => <span>{getMonthDate(text)}</span>,
    },
    {
      title: "Invoice Type",
      dataIndex: "invoice_type",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Buyer Name",
      dataIndex: "buyer_business_name",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Buyer NTN/CNIC",
      dataIndex: "buyer_ntn_cnic",
      render: (text) => <span>{text || '-'}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => {
        const statusColor = text === "PENDING" ? "badge-linegray" : text === "SUBMITTED" ? "badge-linesuccess" : text === "VALID" ? "badge-linewarning" : "badge-linedanger";
        return <span className={`badge ${statusColor} text-capitalize`}>{text}</span>;
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      render: (text) => (
        <div style={{ width: '250px', whiteSpace: 'break-spaces' }}>{text || '-'}</div>
      ),
    },
    {
      title: "FBR Invoice No",
      dataIndex: "fbr_invoice_number",
      render: (text) => <div>{text ? `FBR # ${text}` : '-'}</div>,
    },
  ];

  useEffect(() => {
    getInvoices();
  }, [searchParams]);

  return (
    <Fragment>

      <InvoiceForm getData={() => getInvoices()} />
      <ExportInvoices />

      {/* ── Bulk Result Modal ── */}
      <Modal
        title={resultModalTitle}
        open={resultModalOpen}
        onCancel={() => setResultModalOpen(false)}
        footer={[
          <button key="close" className="btn btn-sm btn-secondary" onClick={() => setResultModalOpen(false)}>
            Close
          </button>
        ]}
        width={560}
      >
        {bulkResult && (
          <div>
            <div className="d-flex gap-3 mb-3">
              <div style={{ background: '#d1fae5', borderRadius: 8, padding: '10px 20px', flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#065f46' }}>{bulkResult.success?.length ?? 0}</div>
                <div style={{ fontSize: 12, color: '#065f46' }}>Succeeded</div>
              </div>
              <div style={{ background: '#fee2e2', borderRadius: 8, padding: '10px 20px', flex: 1, textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#991b1b' }}>{bulkResult.failed?.length ?? 0}</div>
                <div style={{ fontSize: 12, color: '#991b1b' }}>Failed</div>
              </div>
            </div>

            {bulkResult.success?.length > 0 && (
              <div className="mb-3">
                <div style={{ fontWeight: 600, marginBottom: 6, color: '#065f46' }}>
                  <CheckCircle size={14} style={{ marginRight: 6 }} />Succeeded
                </div>
                <div style={{ maxHeight: 160, overflowY: 'auto', background: '#f0fdf4', borderRadius: 6, padding: 10 }}>
                  {bulkResult.success.map((item, i) => (
                    <div key={i} style={{ fontSize: 13, marginBottom: 2 }}>
                      {item.invoice_ref_no || `#${item.id}`}
                      {item.fbr_invoice_number && <span style={{ color: '#6b7280', marginLeft: 8 }}>→ FBR {item.fbr_invoice_number}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {bulkResult.failed?.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6, color: '#991b1b' }}>
                  <Trash2 size={14} style={{ marginRight: 6 }} />Failed
                </div>
                <div style={{ maxHeight: 180, overflowY: 'auto', background: '#fef2f2', borderRadius: 6, padding: 10 }}>
                  {bulkResult.failed.map((item, i) => (
                    <div key={i} style={{ fontSize: 13, marginBottom: 4 }}>
                      <span style={{ fontWeight: 500 }}>{item.invoice_ref_no || `#${item.id}`}</span>
                      <span style={{ color: '#b91c1c', marginLeft: 8 }}>— {item.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <h4>Invoice List</h4>
                <h6>Manage your invoices</h6>
              </div>
            </div>
            {userData?.role === 'user' && (
              <Fragment>
                <div className="page-btn">
                  <Link to={routes.addInvoice} className="btn btn-added">
                    <PlusCircle className="me-2" />Add Invoice
                  </Link>
                </div>
                <div className="page-btn">
                  <Link to="#" className="btn btn-added btn-warning" data-bs-toggle="modal" data-bs-target="#open-modal">
                    <Upload className="me-2" />Import Invoices
                  </Link>
                </div>
                <div className="page-btn">
                  <Link to="#" className="btn btn-added color" data-bs-toggle="modal" data-bs-target="#export-modal">
                    <Download className="me-2" />Export Invoices
                  </Link>
                </div>
              </Fragment>
            )}
          </div>

          <div className="card table-list-card">
            <div className="card-body">
              {/* ── Bulk Action Toolbar ── */}
              {selectedIds.length > 0 && (
                <div
                  style={{
                    background: '#EFF6FF',
                    border: '1px solid #BFDBFE',
                    borderRadius: 8,
                    padding: '10px 18px',
                    marginBottom: 12,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <CheckSquare size={16} color="#2563EB" style={{ flexShrink: 0 }} />
                  <span style={{ fontWeight: 600, color: '#1e3a5f', fontSize: 14 }}>
                    {selectedIds.length} invoice{selectedIds.length > 1 ? 's' : ''} selected
                  </span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-sm btn-warning btn-wave"
                      disabled={bulkLoading}
                      onClick={handleBulkValidate}
                    >
                      <CheckCircle size={14} className="me-1" />
                      Validate Selected
                    </button>
                    <button
                      className="btn btn-sm btn-success btn-wave"
                      disabled={bulkLoading}
                      onClick={handleBulkSubmit}
                    >
                      <Send size={14} className="me-1" />
                      Submit Selected
                    </button>
                    <button
                      className="btn btn-sm btn-danger btn-wave"
                      disabled={bulkLoading}
                      onClick={handleBulkDelete}
                    >
                      <Trash2 size={14} className="me-1" />
                      Delete Selected
                    </button>
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      disabled={bulkLoading}
                      onClick={() => setSelectedIds([])}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {/* ── Filters ── */}
              <div className="table-top" style={{ display: 'block', marginBottom: '10px', padding: '18px 18px 0' }}>
                <div className="d-flex align-items-flex-end justify-content-between gap-3 flex-wrap">
                  <div>
                    <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: 600 }}>Search</label>
                    <div className="search-set">
                      <div className="search-input">
                        <input
                          type="text"
                          defaultValue={searchText}
                          placeholder="Search by invoice ref, buyer, NTN/CNIC"
                          className="form-control form-control-sm formsearch"
                          style={{ width: '300px' }}
                          onChange={(e) => handlePaginationFilter({ search: e.target.value })}
                        />
                        <Link to className="btn btn-searchset">
                          <i data-feather="search" className="feather-search" />
                        </Link>
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-flex-end gap-3 flex-wrap">
                    <div>
                      <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: 600 }}>Status</label>
                      <div className="input-blocks mb-0">
                        <StopCircle className="info-img" />
                        <Select
                          classNamePrefix="react-select"
                          className="img-select"
                          options={invoiceStatuses}
                          placeholder="All Statuses"
                          value={invoiceStatuses.find((item) => item.value === status) || null}
                          onChange={(e) => handlePaginationFilter({ status: e.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: 600 }}>Sort By</label>
                      <div className="input-blocks mb-0">
                        <Sliders className="info-img" />
                        <Select
                          classNamePrefix="react-select"
                          className="img-select"
                          options={sortOptions}
                          placeholder="Sort By"
                          value={sortOptions.find((item) => item.value === sort) || sortOptions[0]}
                          onChange={(e) => handlePaginationFilter({ sort: e.value })}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="form-label mb-1" style={{ fontSize: '12px', fontWeight: 600 }}>Invoice Date</label>
                      <div className="input-blocks mb-0">
                        <div className="input-groupicon calender-input">
                          <Calendar className="info-img" />
                          <RangePicker
                            value={startDate && [dayjs(startDate), dayjs(endDate)]}
                            onChange={(date) => handlePaginationFilter({ from: date ? dayjs(date[0]).format('YYYY-MM-DD') : '', to: date ? dayjs(date[1]).format('YYYY-MM-DD') : '' })}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="table-responsive">
                <Table
                  loading={loader}
                  columns={columns}
                  dataSource={invoices}
                  totalCount={totalCount}
                  pageSize={pageLimit}
                  currentPage={currentPage}
                  onChange={(page, pageSize) => handlePaginationFilter({ page: page, limit: pageSize })}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default Invoices;
